import * as ol from 'openlayers';
import 'openlayers/dist/ol.css';
// todo, make generic geo element from this.
import { EmployeeDataType } from './../../../data-management/data-sources/sources/employees-data-source';
import { HtmlComponent } from './../html-component';
import { DataMergerLogicalAnd } from './../../../data-management/data-filters/mergers/data-merger-logical-and';
import { MapStyles } from './map-styles';
import { MapDrawInteractionUI } from './map-ui/map-draw-interaction-ui';
import { MapPopup } from './map-popup/map-popup';
import { SelectUserCommandParameters } from './../../commands/select-user-command-executor';
import { LocationsSelectorGroupComponent } from './../locations-selector-group/locations-selector-group-component';
import { EmployeesDataSource } from './../../../data-management/data-sources/sources/employees-data-source';

import { Gui } from './../../gui';

// import html
import template from './map.hogan.html';

export class Map extends HtmlComponent {
    private usersDataSource: EmployeesDataSource;
    public clickInteraction: any = false;

    public dataIdsToFeatures: any[] = [];
    private usersLayer: any;
    private usersHeatLayer: any;
    private usersLayerSource: any;
    private backgroundLayer: any;
    private merger: DataMergerLogicalAnd;
    private locationsSelector: LocationsSelectorGroupComponent;

    public drawUI: MapDrawInteractionUI;
    public olMap: any;
    public mapStyles: MapStyles;
    public popup: any;
    private previousParameters: SelectUserCommandParameters;

    constructor(private gui: Gui) {
        super(template, {});
        this.mapStyles = new MapStyles();
        this.merger = gui.app.dataFiltersManager.allFiltersMerger;
        this.usersDataSource = gui.app.dataSourceLoader.employeesDataSource;
        MapStyles.usersDataSource = this.usersDataSource;
    }

    public init() {
        this.initMap();
        this.populateMap();
        this.checkMapNeedsResize();
        this.initUpdates();
        this.initMapDrawInteractionUI();
        this.initSelectInteraction();
        this.popup = new MapPopup(this.gui);
        this.popup.addToJqueryTag('#map-target', this);
        this.handleFilterUpdate();
        this.appendLocationSelectors();
    }

    private initUpdates() {
        this.merger.filterUpdatedEvent.attach(() => this.handleFilterUpdate());
    }

    private appendLocationSelectors() {
        this.locationsSelector = new LocationsSelectorGroupComponent(this.gui);
        this.locationsSelector.addToJqueryTag('.locations', this);
    }

    private handleFilterUpdate() {
        this.merger.filteredData.forEach((el: EmployeeDataType) => {
            MapStyles.userDataIdsToVisibility[el._internal_datasource_id_] = this.merger.anyFilterApplied;
            this.dataIdsToFeatures[el._internal_datasource_id_].changed();
        });
        this.merger.hiddenData.forEach((el: EmployeeDataType) => {
            MapStyles.userDataIdsToVisibility[el._internal_datasource_id_] = false;
            this.dataIdsToFeatures[el._internal_datasource_id_].changed();
        });
        this.olMap.render();
    }

    private initMap() {
        this.initBackgroundLayer();
        this.initUsersSource();
        this.initVectorLayer();
        this.initVectorHeatmapLayer();

        this.olMap = new ol.Map({
            layers: [this.backgroundLayer, this.usersHeatLayer, this.usersLayer],
            target: 'map-target',
            view: new ol.View({
                center: [502649.89800331905, 6617612.16081742],
                zoom: 8,
                maxZoom: 11
            })
        });
    }

    private initSelectInteraction() {
        this.clickInteraction = new ol.interaction.Select({
            style: MapStyles.returnSelectedStyle,
            layers: [this.usersHeatLayer],
            multi: true
        });

        this.clickInteraction.on('select', (e: ol.interaction.Select.Event) => {
            if (
                this.drawUI.popupInteractionTime &&
                !this.drawUI.isDraggingCircle &&
                this.drawUI.popupInteractionTime < new Date().getTime() / 1000 - 1
            ) {
                if (e.selected.length > 0) {
                    if (e.selected.length > 1) {
                        const coordinate = (e.selected[0].getGeometry() as ol.geom.Point).getCoordinates();
                        const pixel = this.olMap.getPixelFromCoordinate(coordinate);

                        // We gaan er niet meer van uit dat de volgorde klopt. Dit is robuuster. Maar why oh why die interne ids?
                        const internalIds = e.selected.map(el => el.getId());
                        this.popup.showAndPopulate(
                            this.usersDataSource.data.filter(
                                employee => internalIds.indexOf(employee._internal_datasource_id_) !== -1
                            ),
                            { x: pixel[0], y: pixel[1] }
                        );
                    } else {
                        this.popup.hide();
                        this.gui.commandManager.selectUserCommandExecutor.requestExecute({
                            userId: parseInt(e.selected[0].getId().toString())
                        });
                    }
                } else {
                    this.popup.hide();
                }
            } else {
                // reset highlight
                this.setSelected(this.previousParameters);
            }
        });

        this.gui.commandManager.selectUserCommandExecutor.commandExecuted.attach(
            (parameters: SelectUserCommandParameters) => this.setSelected(parameters)
        );
        this.olMap.addInteraction(this.clickInteraction);
        this.gui.commandManager.selectAccountCommandExecutor.commandExecuted.attach(() =>
            this.clickInteraction.getFeatures().clear()
        );
    }

    private setSelected(el: SelectUserCommandParameters) {
        this.previousParameters = el;

        this.clickInteraction.getFeatures().clear();
        if (el && el.userId) {
            const featurePoint = this.dataIdsToFeatures[el.userId];
            const selectedCollection = this.clickInteraction.getFeatures();
            selectedCollection.push(featurePoint);
            const coordinate = featurePoint.getGeometry().getCoordinates();
            ol.coordinate.toStringHDMS(ol.proj.transform(coordinate, 'EPSG:3857', 'EPSG:4326'), 2);
        }
    }

    private initBackgroundLayer() {
        this.backgroundLayer = new ol.layer.Tile({
            source: new ol.source.XYZ({
                tileSize: [512, 512],
                url:
                    'https://api.mapbox.com/styles/v1/mapbox/light-v9/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoidGVzdC1mbG93ZXIiLCJhIjoiY2lmZTMyemhkMDBnd3Rha25oemM5anhidiJ9.iaeV73ck8rMFCEeKb6ZtCw'
            })
        });
    }

    private initUsersSource() {
        this.usersLayerSource = new ol.source.Vector({});
    }

    private initVectorLayer() {
        // in case we want to cluster, change the distance.
        let clusteredSource = new ol.source.Cluster({ distance: 0, source: this.usersLayerSource });
        // styling

        this.usersLayer = new ol.layer.Vector({
            source: this.usersLayerSource,
            style: MapStyles.returnActiveStyle
        });
    }

    private initVectorHeatmapLayer() {
        // in case we want to cluster, change the distance.
        let clusteredSource = new ol.source.Cluster({ distance: 0, source: this.usersLayerSource });
        // styling

        this.usersHeatLayer = new ol.layer.Vector({
            source: this.usersLayerSource,
            style: MapStyles.returnHeatmapActiveStyle
        });

        this.usersHeatLayer.on('precompose', this.setBlendMode);
        this.usersHeatLayer.on('postcompose', this.resetBlendMode);
    }

    private initMapDrawInteractionUI() {
        this.drawUI = new MapDrawInteractionUI(this, this.gui);
        this.drawUI.addToJqueryTag('#map-target', this);
    }

    private setBlendMode(evt: any) {
        evt.context.globalCompositeOperation = 'hard-light';
    }
    private resetBlendMode(evt: any) {
        evt.context.globalCompositeOperation = 'source-over';
    }

    private checkMapNeedsResize() {
        setTimeout(() => {
            this.olMap.updateSize();
        }, 400);
    }

    private populateMap() {
        this.usersDataSource.data.forEach((user: EmployeeDataType) => {
            let feature = this.makePointFeature(user);
            this.usersLayerSource.addFeature(feature);
        });
        this.olMap.render();
    }

    private makePointFeature(user: EmployeeDataType) {
        let coordinate = ol.proj.transform([user.Longitude, user.Latitude], 'EPSG:4326', 'EPSG:3857');
        const point = new ol.geom.Point(coordinate);
        const feature = new ol.Feature({
            name: user.Name,
            geometry: point,
            id: user.Id
        });
        feature.setId(user._internal_datasource_id_);
        this.dataIdsToFeatures[user._internal_datasource_id_] = feature;
        return feature;
    }
}
